import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BigIntBaseEntity } from './base.entity';

/**
 * PD-17608 — every webhook event Instantly delivered to the tenant, stored raw
 * as it arrived, before anything acts on it.
 *
 * Instantly retries a delivery that did not get a 2xx and its payload carries
 * no event id, so `eventKey` is a stable key computed from the payload (see
 * InstantlyWebhookService.eventKeyOf) and is unique: a retried delivery finds
 * its row and is acknowledged without being stored or acted on twice.
 *
 * `processed` is false until the campaign engine acts on the event (later
 * tickets). The one event acted on at arrival today is `email_bounced`, which
 * marks the address bounced (EmailAddressStatusService) and is then processed.
 *
 * BigInt key: one row per sent email, reply and status change across every
 * running campaign. Not auditable: machine data written at that rate would
 * drown `_audit`.
 */
@Index('IDX_instantly_webhook_event_eventKey', ['eventKey'], { unique: true })
@Index('IDX_instantly_webhook_event_processed', ['processed', 'eventType'], {})
@Index('IDX_instantly_webhook_event_leadEmail', ['leadEmail'], {})
@Entity('instantly_webhook_event', {}, { auditable: false })
export class InstantlyWebhookEventEntity extends BigIntBaseEntity {
	/** SHA-256 (hex) of the event's identifying fields. */
	@Column('char', { name: 'eventKey', length: 64 })
	public eventKey!: string;

	/** Instantly's `event_type`, or a custom label's name as Instantly sends it. */
	@Column('varchar', { name: 'eventType', length: 120 })
	public eventType!: string;

	@Column('varchar', { name: 'leadEmail', length: 320, nullable: true })
	public leadEmail!: string | null;

	/** Instantly's campaign id (`campaign_id`). */
	@Column('varchar', { name: 'externalCampaignId', length: 64, nullable: true })
	public externalCampaignId!: string | null;

	/** When the event happened in Instantly (`timestamp`). */
	@Column('datetime', { name: 'eventDate', nullable: true })
	public eventDate!: Date | null;

	/** The payload exactly as Instantly sent it. */
	@Column('json', { name: 'payload' })
	public payload!: Record<string, unknown>;

	@Column({ type: 'boolean', name: 'processed', width: 1, default: () => "'0'" })
	public processed!: boolean;

	@Column('datetime', { name: 'processedDate', nullable: true })
	public processedDate!: Date | null;
}
